/**
 * GJ Reviews (sections/gj-reviews.liquid): placeholder cards or a star rating summary.
 *
 * The section renders Judge.me's star rating badge hidden, only as a review counter, and the
 * placeholder cards as the default: without JavaScript, without the Judge.me app embed, or while
 * Judge.me loads, nothing changes. Once Judge.me has filled in the badge and reports one or more
 * reviews for the product, the element switches to data-reviews-state="reviews" and the summary
 * card (average, stars, "[count] reviews") is filled in and shown instead of the cards. If it
 * reports 0 reviews, it stays (or goes back to) "empty".
 *
 * Judge.me renders the badge after page load, so a MutationObserver watches for it.
 */

const COUNTER_SELECTOR = '.jdgm-prev-badge[data-number-of-reviews]';

class GjReviewsComponent extends HTMLElement {
  /** @type {MutationObserver | null} */
  observer = null;

  connectedCallback() {
    this.update();
    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-number-of-reviews', 'data-average-rating'],
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
  }

  update() {
    const counter = this.querySelector(COUNTER_SELECTOR);
    const count = Number.parseInt(counter?.getAttribute('data-number-of-reviews') ?? '', 10) || 0;
    const average = Number.parseFloat(counter?.getAttribute('data-average-rating') ?? '') || 0;

    if (count > 0) this.renderSummary(count, average);

    const state = count > 0 ? 'reviews' : 'empty';
    if (this.dataset.reviewsState !== state) this.dataset.reviewsState = state;
  }

  /**
   * Fills in the summary card: the average with one decimal, that many filled stars (rounded), and
   * the count label from the section settings.
   * @param {number} count
   * @param {number} average
   */
  renderSummary(count, average) {
    const summary = this.querySelector('.gj-reviews__summary');
    if (!(summary instanceof HTMLElement)) return;

    const averageText = average.toFixed(1);
    const averageElement = summary.querySelector('[data-average]');
    if (averageElement && averageElement.textContent !== averageText) averageElement.textContent = averageText;

    const label = (count === 1 ? summary.dataset.labelOne : summary.dataset.labelOther) ?? '[count]';
    const countText = label.replace('[count]', String(count));
    const countElement = summary.querySelector('[data-count]');
    if (countElement && countElement.textContent !== countText) countElement.textContent = countText;

    const filled = Math.round(average);
    summary.querySelectorAll('.gj-stars__star path').forEach((path, index) => {
      path.setAttribute('fill', index < filled ? 'currentColor' : 'none');
    });

    const stars = summary.querySelector('.gj-stars');
    stars?.setAttribute('role', 'img');
    stars?.setAttribute('aria-label', `${averageText} / 5`);
    stars?.removeAttribute('aria-hidden');
  }
}

if (!customElements.get('gj-reviews-component')) customElements.define('gj-reviews-component', GjReviewsComponent);
